import React, { useEffect, useState, useMemo } from "react";
import { Loader2, X, Trash2, FileText, Send, Plus, Clock, User as UserIcon, RefreshCw, Hash, CalendarClock, CheckCircle2, Circle, ClipboardList, FileCheck, Receipt, PoundSterling, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "quoted", label: "Quoted" },
  { value: "quote_accepted", label: "Quote Accepted" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "lost", label: "Lost" },
];

const STATUS_COLOURS = {
  new: "bg-blue-50 text-blue-700",
  acknowledged: "bg-sky-50 text-sky-700",
  quoted: "bg-violet-50 text-violet-700",
  quote_accepted: "bg-purple-50 text-purple-700",
  scheduled: "bg-amber-50 text-amber-700",
  in_progress: "bg-orange-50 text-orange-700",
  completed: "bg-emerald-50 text-emerald-700",
  lost: "bg-red-50 text-red-700",
};

function StatusBadge({ status }) {
  const label = STATUSES.find((s) => s.value === status)?.label || status;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${STATUS_COLOURS[status] || "bg-slate-100 text-slate-700"}`}>{label}</span>;
}

export default function AdminEnquiries() {
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState(null);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [quoteFor, setQuoteFor] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [cloneFromQuote, setCloneFromQuote] = useState(null);
  const [quotes, setQuotes] = useState([]); // quotes for the selected enquiry
  const [schedules, setSchedules] = useState([]); // schedules for the selected enquiry
  const [jobNumberFor, setJobNumberFor] = useState(null); // quote pending job number
  const [scheduleFor, setScheduleFor] = useState(null);   // quote pending schedule
  const [enquiryScheduleFor, setEnquiryScheduleFor] = useState(null);  // enquiry-level site visit

  const reloadSelected = async (id) => {
    const r = await api.get("/enquiries");
    const list = r.data;
    setEnquiries(list);
    if (id) {
      const updated = list.find((e) => e.id === id);
      if (updated) setSelected(updated);
      try {
        const qr = await api.get(`/enquiries/${id}/quotes`);
        setQuotes(qr.data || []);
      } catch { setQuotes([]); }
      try {
        const sr = await api.get(`/enquiries/${id}/schedules`);
        setSchedules(sr.data || []);
      } catch { setSchedules([]); }
    }
  };

  const load = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  // Fetch quotes + schedules whenever a new enquiry is opened
  useEffect(() => {
    if (selected?.id) {
      api.get(`/enquiries/${selected.id}/quotes`).then((r) => setQuotes(r.data || [])).catch(() => setQuotes([]));
      api.get(`/enquiries/${selected.id}/schedules`).then((r) => setSchedules(r.data || [])).catch(() => setSchedules([]));
    } else {
      setQuotes([]);
      setSchedules([]);
    }
  }, [selected?.id]);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/enquiries/${id}`, payload);
      toast.success("Updated");
      await reloadSelected(id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry and all linked quotes?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const exportEnquiries = async () => {
    try {
      const params = filter && filter !== "all" ? `?status=${encodeURIComponent(filter)}` : "";
      const res = await api.get(`/enquiries/export.xlsx${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `oec-enquiries-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel export downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Export failed");
    }
  };

  const filtered = useMemo(() =>
    (enquiries || []).filter((e) => filter === "all" ? true : e.status === filter),
    [enquiries, filter]
  );

  return (
    <div className="p-8 lg:p-10" data-testid="page-admin-enquiries">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">CRM</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Enquiries</h1>
          <p className="text-slate-500 mt-2">Full lifecycle from receipt to completion. Every change is timestamped against a user — fully traceable for ISO audits.</p>
        </div>
        <button
          onClick={exportEnquiries}
          disabled={!enquiries || enquiries.length === 0}
          className="inline-flex items-center gap-2 bg-[#0A0F1C] hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs uppercase tracking-wider font-semibold px-4 py-2.5 transition-colors"
          data-testid="export-enquiries-xlsx"
          title={filter === "all" ? "Export all enquiries to Excel" : `Export enquiries filtered to "${filter}" to Excel`}
        >
          <Download className="w-4 h-4" />
          Export to Excel{filter !== "all" ? ` (${filter})` : ""}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)} className={`text-xs uppercase tracking-wider px-3 py-2 font-semibold transition-colors ${filter === s.value ? "bg-[#0A0F1C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-900"}`} data-testid={`filter-${s.value}`}>{s.label}</button>
        ))}
      </div>

      {enquiries === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 py-20 text-center text-slate-400">No enquiries match this filter.</div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-6 py-3 font-medium">Ref</th>
                <th className="px-6 py-3 font-medium">Contact</th>
                <th className="px-6 py-3 font-medium">Service</th>
                <th className="px-6 py-3 font-medium">Assigned</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} onClick={() => setSelected(e)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`enquiry-row-${e.id}`}>
                  <td className="px-6 py-4">
                    {e.ref_no ? (
                      <span className="font-mono text-xs font-semibold text-slate-900" data-testid={`enquiry-ref-${e.id}`}>{e.ref_no}</span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-slate-900">{e.name}</p>
                    <p className="text-xs text-slate-500">{e.email}</p>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{e.service || "General"}</td>
                  <td className="px-6 py-4 text-xs text-slate-600">{e.assigned_to ? userName(e.assigned_to) : "—"}</td>
                  <td className="px-6 py-4"><StatusBadge status={e.status} /></td>
                  <td className="px-6 py-4 text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)} data-testid="enquiry-modal">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex justify-between items-start">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1 flex items-center gap-2">
                  <span>Enquiry {selected.ref_no ? <span className="font-mono font-semibold text-slate-900 normal-case tracking-normal" data-testid="drawer-ref-no">· {selected.ref_no}</span> : null} {selected.latest_quote_ref ? `· ${selected.latest_quote_ref}` : "· no quote"}</span>
                  {selected.source === "email-inbound" && (
                    <span className="bg-sky-50 text-sky-700 px-2 py-0.5 normal-case tracking-normal text-[10px] font-semibold" data-testid="badge-email-inbound">📧 Email</span>
                  )}
                </p>
                <h2 className="font-display text-2xl font-bold">{selected.name}</h2>
                <p className="text-sm text-slate-500 mt-1">Received {new Date(selected.created_at).toLocaleString()}</p>
                {selected.inbound_subject && (
                  <p className="text-xs text-slate-600 mt-1"><span className="text-slate-400">Subject:</span> {selected.inbound_subject}</p>
                )}
                {selected.forwarded_by_email && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="forwarded-by-line">
                    <span className="bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">↩ Forwarded</span>
                    <span className="ml-2">by {selected.forwarded_by_name || "team member"} &lt;{selected.forwarded_by_email}&gt; — original sender preserved</span>
                  </p>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900" data-testid="close-enquiry"><X /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Email</p><p><a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></p></div>
                {selected.phone && <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Phone</p><p>{selected.phone}</p></div>}
                {selected.company && <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Company</p><p>{selected.company}</p></div>}
                <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Service · Sector</p><p>{selected.service} · {selected.sector || "—"}</p></div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Message</p>
                <p className="text-slate-700 whitespace-pre-wrap text-sm bg-slate-50 p-3">{selected.message}</p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s.value} onClick={() => patch(selected.id, { status: s.value })} className={`text-xs uppercase tracking-wider px-3 py-2 font-semibold transition-colors ${selected.status === s.value ? "gs-gradient text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`set-status-${s.value}`}>{s.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AssignField label="Acknowledged by" value={selected.acknowledged_by} when={selected.acknowledged_at} users={users} onChange={(v) => patch(selected.id, { acknowledged_by: v })} />
                <AssignField label="Quoted by" value={selected.quoted_by} when={selected.quoted_at} users={users} onChange={(v) => patch(selected.id, { quoted_by: v })} />
                <AssignField label="Scheduled by" value={selected.scheduled_by} when={selected.scheduled_at} users={users} onChange={(v) => patch(selected.id, { scheduled_by: v })} />
                <AssignField label="Assigned surveyor" value={selected.assigned_to} when={null} users={users} onChange={(v) => patch(selected.id, { assigned_to: v })} testid="assigned-to" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Scheduled date">
                  <input type="date" defaultValue={(selected.scheduled_for || "").substring(0, 10)} onBlur={(e) => e.target.value !== (selected.scheduled_for || "").substring(0, 10) && patch(selected.id, { scheduled_for: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="scheduled-for" />
                </Field>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Internal notes</p>
                <textarea rows={4} defaultValue={selected.notes || ""} onBlur={(e) => e.target.value !== (selected.notes || "") && patch(selected.id, { notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="enquiry-notes" />
              </div>

              {Array.isArray(selected.attachments) && selected.attachments.length > 0 && (
                <div className="border-t border-slate-100 pt-4" data-testid="enquiry-attachments">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Attachments ({selected.attachments.length})</p>
                  <div className="space-y-2">
                    {selected.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${selected.id}/attachments/${a.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between gap-3 border border-slate-200 p-3 bg-slate-50 hover:border-[#1FA59A] hover:bg-white transition-colors"
                        data-testid={`attachment-${a.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{a.original_name}</p>
                            <p className="text-xs text-slate-500">{(a.size / 1024).toFixed(1)} KB · {a.source === "email-inbound" ? "📧 from email" : "Uploaded"}</p>
                          </div>
                        </div>
                        <span className="text-xs text-[#1FA59A] font-semibold flex-shrink-0">Download →</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2 flex-wrap">
                <button onClick={() => { setEditingQuote(null); setCloneFromQuote(null); setQuoteFor(selected); }} className="gs-gradient text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:opacity-90 text-sm" data-testid="open-quote-builder">
                  <FileText className="w-4 h-4" /> Build new quote
                </button>
                <button onClick={() => setEnquiryScheduleFor(selected)} className="border-2 border-[#1FA59A] text-[#1FA59A] hover:bg-[#1FA59A] hover:text-white font-semibold px-5 py-2.5 flex items-center gap-2 text-sm transition-colors" data-testid="schedule-site-visit">
                  <CalendarClock className="w-4 h-4" /> Schedule site visit
                </button>
              </div>

              {quotes.length > 0 && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Quotes ({quotes.length})</p>
                  <div className="space-y-2">
                    {quotes.map((q) => (
                      <div key={q.id} className="flex items-center justify-between gap-3 border border-slate-200 p-3 bg-slate-50 flex-wrap" data-testid={`quote-card-${q.id}`}>
                        <div className="flex-1 min-w-[180px]">
                          <p className="font-mono text-xs text-slate-900 font-semibold">{q.reference}</p>
                          <p className="text-xs text-slate-500">£{(q.total || 0).toFixed(2)} inc. VAT · valid until {q.valid_until}</p>
                          {q.job_number && (
                            <p className="text-xs text-emerald-700 font-semibold mt-1 flex items-center gap-1" data-testid={`quote-job-${q.id}`}>
                              <Hash className="w-3 h-3" /> {q.job_number}
                            </p>
                          )}
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${q.status === "accepted" ? "bg-emerald-50 text-emerald-700" : q.status === "rejected" ? "bg-red-50 text-red-700" : q.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>{q.status}</span>
                        <a href={`/admin/quotes/${q.id}`} target="_blank" rel="noreferrer" className="text-xs text-[#1FA59A] font-semibold hover:underline whitespace-nowrap">View / Print</a>
                        {(q.status === "draft" || q.status === "sent") && (
                          <button onClick={() => { setEditingQuote(q); setCloneFromQuote(null); setQuoteFor(selected); }} className="text-xs border border-slate-300 hover:border-[#1FA59A] hover:text-[#1FA59A] px-3 py-1.5 font-semibold" data-testid={`edit-quote-${q.id}`}>Edit</button>
                        )}
                        {q.status === "rejected" && (
                          <button onClick={() => { setEditingQuote(null); setCloneFromQuote(q); setQuoteFor(selected); }} className="text-xs border border-amber-600 text-amber-700 hover:bg-amber-50 px-3 py-1.5 font-semibold flex items-center gap-1" data-testid={`revise-quote-${q.id}`}>
                            <RefreshCw className="w-3 h-3" /> Issue revised quote
                          </button>
                        )}
                        {q.status === "accepted" && !q.job_number && (
                          <button onClick={() => setJobNumberFor(q)} className="text-xs border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 font-semibold flex items-center gap-1" data-testid={`add-job-number-${q.id}`}>
                            <Hash className="w-3 h-3" /> Add job no.
                          </button>
                        )}
                        {q.status === "accepted" && q.job_number && (
                          <button onClick={() => setScheduleFor(q)} className="text-xs gs-gradient text-white px-3 py-1.5 font-semibold flex items-center gap-1 hover:opacity-90" data-testid={`schedule-job-${q.id}`}>
                            <CalendarClock className="w-3 h-3" /> Schedule job
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {schedules.length > 0 && (
                <div className="border-t border-slate-100 pt-4" data-testid="schedules-block">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Scheduled jobs ({schedules.length})</p>
                  <div className="space-y-2">
                    {schedules.map((s) => (
                      <div key={s.id} className="border border-slate-200 p-3 bg-white text-sm flex items-start justify-between gap-3" data-testid={`schedule-card-${s.id}`}>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#1FA59A]" /> {s.staff_name}</p>
                          <p className="text-xs text-slate-600 mt-1">{new Date(s.start_iso).toLocaleString()} → {new Date(s.end_iso).toLocaleString()}</p>
                          {s.location && <p className="text-xs text-slate-500 mt-0.5">📍 {s.location}</p>}
                          {s.notes && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{s.notes}</p>}
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${s.email_sent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{s.email_sent ? "Invite sent" : "Invite pending"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <JobTracker enquiry={selected} users={users} onChange={() => reloadSelected(selected.id)} />

              <Timeline entries={selected.timeline || []} />

              {user?.role === "admin" && (
                <button onClick={() => removeEnquiry(selected.id)} className="flex items-center gap-2 text-red-600 hover:text-red-800 text-sm font-semibold pt-4 border-t border-slate-100 w-full" data-testid="delete-enquiry">
                  <Trash2 className="w-4 h-4" /> Delete enquiry &amp; linked quotes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {quoteFor && <QuoteBuilder enquiry={quoteFor} existingQuote={editingQuote} cloneFrom={cloneFromQuote} onClose={() => { setQuoteFor(null); setEditingQuote(null); setCloneFromQuote(null); }} onCreated={(q) => { const wasEdit = !!editingQuote; const wasClone = !!cloneFromQuote; setQuoteFor(null); setEditingQuote(null); setCloneFromQuote(null); toast.success(wasEdit ? `Quote ${q.reference} updated` : wasClone ? `Revised quote ${q.reference} issued` : `Quote ${q.reference} created`); reloadSelected(quoteFor.id); }} />}

      {jobNumberFor && (
        <JobNumberModal
          quote={jobNumberFor}
          onClose={() => setJobNumberFor(null)}
          onSaved={async () => { setJobNumberFor(null); await reloadSelected(selected.id); }}
        />
      )}

      {scheduleFor && (
        <ScheduleJobModal
          quote={scheduleFor}
          users={users}
          onClose={() => setScheduleFor(null)}
          onSaved={async () => { setScheduleFor(null); await reloadSelected(selected.id); }}
        />
      )}

      {enquiryScheduleFor && (
        <ScheduleSiteVisitModal
          enquiry={enquiryScheduleFor}
          users={users}
          onClose={() => setEnquiryScheduleFor(null)}
          onSaved={async () => { setEnquiryScheduleFor(null); await reloadSelected(selected.id); }}
        />
      )}
    </div>
  );
}

function AssignField({ label, value, when, users, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</p>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid={testid}>
        <option value="">— unassigned —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
      </select>
      {when && <p className="text-[10px] text-slate-500 mt-1"><Clock className="w-3 h-3 inline mr-1" />{new Date(when).toLocaleString()}</p>}
    </div>
  );
}

function Timeline({ entries }) {
  if (!entries?.length) return null;
  return (
    <div className="border-t border-slate-100 pt-5">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Audit timeline</p>
      <ol className="space-y-3 text-xs">
        {[...entries].reverse().map((e, i) => (
          <li key={i} className="flex gap-3" data-testid="timeline-entry">
            <div className="flex-shrink-0 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center"><UserIcon className="w-4 h-4 text-slate-500" /></div>
            <div className="flex-1">
              <p className="text-slate-900"><strong>{e.by_user_name}</strong> · {e.action.replace(/_/g, " ")}</p>
              {e.note && <p className="text-slate-600 mt-0.5">{e.note}</p>}
              {e.fields_changed?.length > 0 && <p className="text-slate-500 mt-0.5">Changed: {e.fields_changed.join(", ")}</p>}
              <p className="text-slate-400 mt-0.5">{new Date(e.at).toLocaleString()}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function QuoteBuilder({ enquiry, existingQuote = null, cloneFrom = null, onClose, onCreated }) {
  const isEditMode = !!existingQuote;
  const isCloneMode = !!cloneFrom && !existingQuote;
  const seedSource = existingQuote || cloneFrom;
  const defaultService = (seedSource?.service) || enquiry?.service || "Asbestos";

  // Seed sections from either existing/cloned quote (services or legacy line_items) or a single empty section
  const seedSections = () => {
    if (seedSource?.services && seedSource.services.length) return seedSource.services.map(s => ({ ...s, line_items: s.line_items?.length ? s.line_items.map(li => ({ ...li })) : [{ description: "", qty: 1, unit_price: 0 }] }));
    if (seedSource?.line_items?.length) return [{ service: defaultService, scope: seedSource.scope || "", line_items: seedSource.line_items.map(li => ({ ...li })) }];
    return [{ service: defaultService, scope: `Provision of ${defaultService.toLowerCase()} services for ${enquiry?.company || enquiry?.name}, as outlined in the enquiry dated ${enquiry ? new Date(enquiry.created_at).toLocaleDateString() : ""}.`, line_items: [{ description: defaultService, qty: 1, unit_price: 0 }] }];
  };

  const [sections, setSections] = useState(seedSections());
  const [intro, setIntro] = useState(seedSource?.scope || "");
  const [notes, setNotes] = useState(isCloneMode ? "" : (existingQuote?.notes || ""));
  const [validDays, setValidDays] = useState(14);
  const [busy, setBusy] = useState(false);

  const subtotal = sections.reduce((sum, s) => sum + s.line_items.reduce((ss, li) => ss + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0), 0);
  const vat = subtotal * 0.2;
  const total = subtotal + vat;

  const addSection = () => setSections([...sections, { service: "Asbestos", scope: "", line_items: [{ description: "", qty: 1, unit_price: 0 }] }]);
  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));
  const updateSection = (idx, key, val) => { const cp = [...sections]; cp[idx][key] = val; setSections(cp); };
  const addLine = (sidx) => { const cp = [...sections]; cp[sidx].line_items.push({ description: "", qty: 1, unit_price: 0 }); setSections(cp); };
  const updateLine = (sidx, lidx, key, val) => { const cp = [...sections]; cp[sidx].line_items[lidx][key] = val; setSections(cp); };
  const removeLine = (sidx, lidx) => { const cp = [...sections]; cp[sidx].line_items = cp[sidx].line_items.filter((_, i) => i !== lidx); setSections(cp); };

  const buildPayload = () => ({ scope: intro, services: sections, notes, valid_days: Number(validDays) || 14 });

  const save = async () => {
    if (sections.length === 0) return toast.error("Add at least one service section");
    if (subtotal === 0) return toast.error("Add a price to at least one line item");
    setBusy(true);
    try {
      let res;
      if (isEditMode) {
        res = await api.put(`/quotes/${existingQuote.id}`, buildPayload());
      } else {
        res = await api.post(`/enquiries/${enquiry.id}/quote`, buildPayload());
      }
      onCreated(res.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const preview = () => {
    const enc = encodeURIComponent(JSON.stringify({
      enquiry: enquiry ? { name: enquiry.name, email: enquiry.email, company: enquiry.company, phone: enquiry.phone, service: enquiry.service, created_at: enquiry.created_at } : null,
      quote: { reference: isEditMode ? existingQuote.reference : (isCloneMode ? `${cloneFrom.reference}-REV` : "PREVIEW"), scope: intro, services: sections, subtotal, vat, total, valid_until: new Date(Date.now() + (Number(validDays) || 14) * 86400000).toISOString().slice(0, 10), notes, created_at: new Date().toISOString() },
    }));
    window.open(`/admin/quotes/preview?preview=${enc}`, "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="quote-builder">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{isEditMode ? `Edit · ${existingQuote.reference}` : isCloneMode ? `Revising · was ${cloneFrom.reference}` : "New quote"}</p>
            <h2 className="font-display text-2xl font-bold">{isEditMode ? "Edit quote" : isCloneMode ? `Revised quote for ${enquiry?.name}` : `Quote for ${enquiry?.name}`}</h2>
            {enquiry && <p className="text-sm text-slate-500">{enquiry.company || ""} · {enquiry.email}</p>}
            {isCloneMode && cloneFrom.rejection?.reason && (
              <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 px-3 py-2 max-w-xl">
                <strong>Client's reason for declining:</strong> {cloneFrom.rejection.reason}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X /></button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Quote introduction (optional — appears above the service sections)</label>
            <textarea rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" placeholder="e.g. Following our discussion on…" data-testid="quote-intro" />
          </div>

          {sections.map((sec, sidx) => {
            const secSubtotal = sec.line_items.reduce((s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0);
            return (
              <div key={sidx} className="border border-slate-200 p-4 bg-slate-50" data-testid={`quote-section-${sidx}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Service section #{sidx + 1}</label>
                    <select value={sec.service} onChange={(e) => updateSection(sidx, "service", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1FA59A]" data-testid={`section-service-${sidx}`}>
                      {QUOTE_SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {sections.length > 1 && (
                    <button onClick={() => removeSection(sidx)} className="text-red-600 hover:text-red-800 mt-7" title="Remove section"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="mb-3">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Brief scope for this service</label>
                  <textarea rows={3} value={sec.scope} onChange={(e) => updateSection(sidx, "scope", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" placeholder="What's included, methodology, deliverables…" data-testid={`section-scope-${sidx}`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Cost lines</label>
                  <div className="space-y-2">
                    {sec.line_items.map((li, lidx) => (
                      <div key={lidx} className="grid grid-cols-12 gap-2 items-center">
                        <input value={li.description} onChange={(e) => updateLine(sidx, lidx, "description", e.target.value)} placeholder="Description" className="col-span-7 border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1FA59A]" data-testid={`section-${sidx}-li-desc-${lidx}`} />
                        <input type="number" min={0} step="0.01" value={li.qty} onChange={(e) => updateLine(sidx, lidx, "qty", e.target.value)} placeholder="Qty" className="col-span-2 border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1FA59A]" />
                        <input type="number" min={0} step="0.01" value={li.unit_price} onChange={(e) => updateLine(sidx, lidx, "unit_price", e.target.value)} placeholder="£" className="col-span-2 border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1FA59A]" data-testid={`section-${sidx}-li-price-${lidx}`} />
                        <button onClick={() => removeLine(sidx, lidx)} className="col-span-1 text-red-600 hover:text-red-800" disabled={sec.line_items.length <= 1}><Trash2 className="w-4 h-4 mx-auto" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <button onClick={() => addLine(sidx)} className="text-xs text-[#1FA59A] hover:underline flex items-center gap-1" data-testid={`section-${sidx}-add-line`}><Plus className="w-3 h-3" /> Add cost line</button>
                    <p className="text-xs font-semibold text-slate-700">Section subtotal: £{secSubtotal.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            );
          })}

          <button onClick={addSection} className="w-full border border-dashed border-slate-400 hover:border-[#1FA59A] py-3 text-sm font-semibold text-slate-600 hover:text-[#1FA59A] flex items-center justify-center gap-2" data-testid="add-section">
            <Plus className="w-4 h-4" /> Add another service to this quote
          </button>

          <div className="bg-slate-900 text-white p-5 grid grid-cols-3 gap-4 text-sm">
            <div><p className="text-[10px] uppercase tracking-wider text-white/60">Subtotal</p><p className="font-semibold text-lg">£{subtotal.toFixed(2)}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-white/60">VAT @ 20%</p><p className="font-semibold text-lg">£{vat.toFixed(2)}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider text-[#7DD3C7]">Total</p><p className="font-bold text-2xl gs-gradient-text">£{total.toFixed(2)}</p></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Valid for (days)</label>
              <input type="number" min={1} max={120} value={validDays} onChange={(e) => setValidDays(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Internal notes (not on quote)</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 p-6 flex justify-between gap-3 sticky bottom-0 bg-white">
          <button onClick={preview} className="border border-slate-300 hover:border-slate-900 text-slate-700 font-semibold px-5 py-2.5 text-sm" data-testid="quote-preview">Preview</button>
          <button onClick={save} disabled={busy} className="gs-gradient text-white font-bold px-6 py-2.5 flex items-center gap-2 hover:opacity-90 disabled:opacity-50" data-testid="quote-create">
            {busy ? "Saving…" : isEditMode ? <><Send className="w-4 h-4" /> Save changes</> : isCloneMode ? <><Send className="w-4 h-4" /> Issue revised quote</> : <><Send className="w-4 h-4" /> Create quote &amp; mark Quoted</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Service options for the quote sections — pulled from the OEC service catalogue
const QUOTE_SERVICE_OPTIONS = [
  "Asbestos",
  "Asbestos Management Survey",
  "Asbestos Refurbishment & Demolition Survey",
  "Asbestos Re-inspection",
  "Asbestos Bulk Sampling",
  "Asbestos Air Monitoring & 4-Stage Clearance",
  "Fire Safety",
  "Fire Risk Assessment (PAS 79-1)",
  "Fire Risk Assessment (PAS 79-2)",
  "Fire Door Inspection",
  "Compartmentation Survey",
  "Water Hygiene",
  "Legionella Risk Assessment",
  "Damp & Mould Survey",
  "Mould Remediation",
  "Compliance & Building Safety",
  "HHSRS Assessment",
  "Remedial Works",
  "Other / Bespoke",
];

function Field({ label, children }) {
  return <div><p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</p>{children}</div>;
}

function JobTracker({ enquiry, users, onChange }) {
  const [busy, setBusy] = useState(false);
  // Surveyors: staff + manager + admin users that are active
  const surveyors = useMemo(
    () => (users || []).filter((u) => u.is_active !== false && ["staff", "manager", "admin"].includes(u.role)),
    [users]
  );
  const [invoiceNumber, setInvoiceNumber] = useState(enquiry.invoice_number || "");

  // Re-sync when the parent enquiry changes (e.g. after a reload)
  useEffect(() => { setInvoiceNumber(enquiry.invoice_number || ""); }, [enquiry.id, enquiry.invoice_number]);

  const patch = async (body, optimisticToast) => {
    setBusy(true);
    try {
      await api.patch(`/enquiries/${enquiry.id}/tracker`, body);
      if (optimisticToast) toast.success(optimisticToast);
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleSurveyor = (e) => patch({ surveyor_id: e.target.value || "" }, e.target.value ? "Surveyor assigned" : "Surveyor cleared");
  const toggleMilestone = (key, currentValue, label) => patch({ [key]: !currentValue }, !currentValue ? `${label} ✓` : `${label} unticked`);
  const saveInvoiceNumber = () => {
    if (invoiceNumber === (enquiry.invoice_number || "")) return;
    patch({ invoice_number: invoiceNumber }, "Invoice number saved");
  };

  const milestones = [
    { key: "job_completed", label: "Job completed", icon: ClipboardList },
    { key: "report_issued", label: "Report issued", icon: FileCheck },
    { key: "invoice_sent", label: "Invoice sent", icon: Receipt },
    { key: "invoice_paid", label: "Invoice paid", icon: PoundSterling },
  ];

  return (
    <div className="border-t border-slate-100 pt-4" data-testid="job-tracker">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Job tracker</p>
      <div className="bg-slate-50 border border-slate-200 p-4 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Assigned surveyor</label>
          <select
            value={enquiry.surveyor_id || ""}
            onChange={toggleSurveyor}
            disabled={busy}
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A] disabled:opacity-60 bg-white"
            data-testid="tracker-surveyor"
          >
            <option value="">— Unassigned —</option>
            {surveyors.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.role}){u.job_title ? ` · ${u.job_title}` : ""}</option>
            ))}
          </select>
          {enquiry.surveyor_email && (
            <p className="text-xs text-slate-500 mt-1">📧 {enquiry.surveyor_email}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {milestones.map((m) => {
            const checked = !!enquiry[m.key];
            const stampedBy = enquiry[`${m.key}_by_name`];
            const stampedAt = enquiry[`${m.key}_at`];
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => toggleMilestone(m.key, checked, m.label)}
                disabled={busy}
                className={`text-left p-3 border transition-colors flex items-start gap-3 disabled:opacity-60 ${checked ? "bg-emerald-50 border-emerald-300 hover:border-emerald-500" : "bg-white border-slate-200 hover:border-slate-400"}`}
                data-testid={`tracker-${m.key}`}
              >
                {checked
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  : <Circle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                }
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold flex items-center gap-2 ${checked ? "text-emerald-900" : "text-slate-700"}`}>
                    <Icon className="w-3.5 h-3.5" /> {m.label}
                  </p>
                  {checked && stampedAt && (
                    <p className="text-[11px] text-slate-500 mt-1 truncate">
                      {new Date(stampedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}{stampedBy ? ` · ${stampedBy}` : ""}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Invoice number (Xero / Sage / etc.)</label>
          <div className="flex gap-2">
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              onBlur={saveInvoiceNumber}
              onKeyDown={(e) => e.key === "Enter" && saveInvoiceNumber()}
              placeholder="e.g. INV-2026-0001"
              disabled={busy}
              className="flex-1 border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1FA59A] disabled:opacity-60 bg-white"
              data-testid="tracker-invoice-number"
            />
            {invoiceNumber !== (enquiry.invoice_number || "") && (
              <button onClick={saveInvoiceNumber} disabled={busy} className="bg-slate-900 text-white text-xs font-semibold px-3 hover:bg-slate-700">Save</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function JobNumberModal({ quote, onClose, onSaved }) {
  const [suffix, setSuffix] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const clean = suffix.trim().toUpperCase();
    if (!clean) return toast.error("Enter the job number suffix");
    if (!/^[A-Z0-9\-/]+$/.test(clean)) return toast.error("Suffix may only contain letters, digits, '-' or '/'");
    setBusy(true);
    try {
      await api.post(`/quotes/${quote.id}/job-number`, { job_number: `OEC-${clean}` });
      toast.success(`Job number OEC-${clean} assigned`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="job-number-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md">
        <div className="p-5 border-b border-slate-200 flex justify-between items-start">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{quote.reference}</p>
            <h3 className="font-display text-xl font-bold">Assign job number</h3>
            <p className="text-xs text-slate-500 mt-1">This will bridge the accepted quote to your surveying software.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Job number</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 bg-slate-100 border border-r-0 border-slate-300 text-slate-700 font-mono text-sm font-semibold">OEC-</span>
              <input
                autoFocus
                value={suffix}
                onChange={(e) => setSuffix(e.target.value.toUpperCase())}
                placeholder="e.g. 24001 or 2026-001"
                className="flex-1 border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1FA59A]"
                data-testid="job-number-suffix"
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">Letters, digits, '-' or '/' only. Must be unique across all OEC jobs.</p>
          </div>
        </div>
        <div className="border-t border-slate-200 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="border border-slate-300 hover:border-slate-900 text-slate-700 font-semibold px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="gs-gradient text-white font-bold px-5 py-2 text-sm disabled:opacity-50" data-testid="job-number-save">
            {busy ? "Saving…" : "Save job number"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleJobModal({ quote, users, onClose, onSaved }) {
  const today = new Date();
  const defaultDate = today.toISOString().slice(0, 10);
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Only staff & manager users can be scheduled — admins skipped to avoid scheduling the boss by accident
  const assignable = useMemo(() => (users || []).filter((u) => u.is_active !== false && (u.role === "staff" || u.role === "manager")), [users]);

  const submit = async () => {
    if (!staffId) return toast.error("Select a staff member");
    if (!date || !startTime || !endTime) return toast.error("Set date, start and end times");
    const startIso = new Date(`${date}T${startTime}:00`).toISOString();
    const endIso = new Date(`${date}T${endTime}:00`).toISOString();
    if (new Date(endIso) <= new Date(startIso)) return toast.error("End time must be after start time");
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/schedule`, {
        staff_user_id: staffId,
        start_iso: startIso,
        end_iso: endIso,
        location,
        notes,
      });
      toast.success(r.data?.email_sent ? "Job scheduled — calendar invite emailed" : "Job scheduled (email send failed, see logs)");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="schedule-job-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex justify-between items-start">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{quote.job_number || quote.reference}</p>
            <h3 className="font-display text-xl font-bold">Schedule job</h3>
            <p className="text-xs text-slate-500 mt-1">An Outlook-compatible .ics calendar invite will be emailed to the staff member.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Staff member</label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-staff">
              <option value="">— select —</option>
              {assignable.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role}) · {u.email}</option>)}
            </select>
            {assignable.length === 0 && <p className="text-xs text-amber-700 mt-2">No active staff/manager users found. Add a staff user under Users first.</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Start</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">End</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-end" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Job address</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={`e.g. ${quote.client_company || quote.client_name || "Site address"}`} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-location" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Notes for staff (optional)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="schedule-notes" />
          </div>
        </div>
        <div className="border-t border-slate-200 p-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border border-slate-300 hover:border-slate-900 text-slate-700 font-semibold px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !staffId} className="gs-gradient text-white font-bold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="schedule-save">
            <CalendarClock className="w-4 h-4" /> {busy ? "Sending…" : "Schedule & email invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

// UK postcode regex for sniffing an address out of the customer's message
const UK_POSTCODE_RE = /([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/i;

function ScheduleSiteVisitModal({ enquiry, users, onClose, onSaved }) {
  // Pre-fill the address: look for a UK postcode in the message and grab the line containing it.
  const presetLocation = (() => {
    const msg = enquiry?.message || "";
    const match = msg.match(UK_POSTCODE_RE);
    if (!match) return "";
    // Find the line containing the postcode and pull a reasonable address window around it
    const idx = msg.indexOf(match[0]);
    const start = Math.max(0, msg.lastIndexOf("\n", idx));
    const end = msg.indexOf("\n", idx);
    return msg.substring(start === 0 ? 0 : start + 1, end === -1 ? msg.length : end).trim().slice(0, 200);
  })();

  // Pre-fill notes with customer briefing
  const presetNotes = [
    enquiry?.phone ? `Customer phone: ${enquiry.phone}` : "",
    enquiry?.company ? `Company: ${enquiry.company}` : "",
    enquiry?.message ? `\nCustomer message:\n${enquiry.message.slice(0, 600)}` : "",
  ].filter(Boolean).join("\n");

  const today = new Date();
  const defaultDate = today.toISOString().slice(0, 10);
  const [staffId, setStaffId] = useState(enquiry?.assigned_to || "");
  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("11:00");
  const [location, setLocation] = useState(presetLocation);
  const [notes, setNotes] = useState(presetNotes);
  const [busy, setBusy] = useState(false);

  const assignable = useMemo(() =>
    (users || []).filter((u) => u.is_active !== false && (u.role === "staff" || u.role === "manager" || u.role === "admin")),
    [users]
  );

  const submit = async () => {
    if (!staffId) return toast.error("Select a surveyor/assessor");
    if (!date || !startTime || !endTime) return toast.error("Set date, start and end times");
    const startIso = new Date(`${date}T${startTime}:00`).toISOString();
    const endIso = new Date(`${date}T${endTime}:00`).toISOString();
    if (new Date(endIso) <= new Date(startIso)) return toast.error("End time must be after start time");
    setBusy(true);
    try {
      const r = await api.post(`/enquiries/${enquiry.id}/schedule`, {
        staff_user_id: staffId,
        start_iso: startIso,
        end_iso: endIso,
        location,
        notes,
      });
      toast.success(r.data?.email_sent ? "Site visit scheduled — calendar invite sent to surveyor" : "Scheduled (email delivery failed — check logs)");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const customerLabel = enquiry?.name || "Customer";
  const refLabel = enquiry?.ref_no || "Enquiry";

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="schedule-site-visit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex justify-between items-start">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{refLabel} · {enquiry?.service || "General"}</p>
            <h3 className="font-display text-xl font-bold">Schedule site visit</h3>
            <p className="text-xs text-slate-500 mt-1">Books <strong>{customerLabel}</strong> in with a surveyor. An Outlook-compatible .ics invite is emailed with the address, service requirements and customer&apos;s message.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" aria-label="Close"><X /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Surveyor / Assessor</label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="site-visit-staff">
              <option value="">— select —</option>
              {assignable.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role}) · {u.email}</option>)}
            </select>
            {assignable.length === 0 && <p className="text-xs text-amber-700 mt-2">No active surveyors found. Add one under Users first.</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="site-visit-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Start</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="site-visit-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">End</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="site-visit-end" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block flex items-center gap-2">
              Site address
              {presetLocation && <span className="text-emerald-700 text-[10px] normal-case tracking-normal">(auto-detected from customer message)</span>}
            </label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Building / street / town / postcode" className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A]" data-testid="site-visit-location" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Briefing notes for surveyor</label>
            <textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Customer context, special instructions, parking, access codes, etc." className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1FA59A] font-mono" data-testid="site-visit-notes" />
            <p className="text-[10px] text-slate-500 mt-1">Customer&apos;s contact details and original message are auto-included in the calendar invite — these notes are for your own internal context.</p>
          </div>
        </div>
        <div className="border-t border-slate-200 p-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border border-slate-300 hover:border-slate-900 text-slate-700 font-semibold px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !staffId} className="gs-gradient text-white font-bold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="site-visit-save">
            <CalendarClock className="w-4 h-4" /> {busy ? "Sending…" : "Schedule & email surveyor"}
          </button>
        </div>
      </div>
    </div>
  );
}

